import React, { FC, useState } from 'react';
import moment, { Moment } from 'moment';
import { Card as AntdCard, Checkbox, DatePicker, Form, Radio } from 'antd';
import { Button } from 'components/UI';
import CloseDayConfirmationModal from 'components/views/OpenDays/CloseDayConfirmationModal';
import * as texts from 'assets/texts/canopyConfiguration';
import { API_DATE_FORMAT, AvailabilityChange, DISPLAY_DATE_FORMAT } from '../types';

interface SeasonFormProps {
  onApply: (change: AvailabilityChange) => Promise<boolean>;
}

type SeasonFormValues = {
  range: [Moment, Moment];
  weekdays?: number[];
  isOpen: boolean;
};

const weekdayOptions = texts.weekdays.map((label, index) => ({
  label,
  value: index + 1,
}));

const SeasonForm: FC<SeasonFormProps> = ({ onApply }) => {
  const [form] = Form.useForm();
  const [pendingChange, setPendingChange] = useState<AvailabilityChange>();
  const [isConfirmationVisible, setIsConfirmationVisible] = useState<boolean>(false);

  const onFinish = (values: SeasonFormValues) => {
    const [startDate, endDate] = values.range;
    setPendingChange({
      startDate: startDate.format(API_DATE_FORMAT),
      endDate: endDate.format(API_DATE_FORMAT),
      weekdays: values.weekdays?.length ? values.weekdays : undefined,
      isOpen: values.isOpen,
    });
    setIsConfirmationVisible(true);
  };

  const confirmChange = async () => {
    if (pendingChange) {
      const applied = await onApply(pendingChange);
      if (applied) {
        form.resetFields();
      }
    }
  };

  const confirmationText = pendingChange
    ? texts.confirmSeason(
        pendingChange.isOpen,
        moment(pendingChange.startDate, API_DATE_FORMAT).format(DISPLAY_DATE_FORMAT),
        moment(pendingChange.endDate, API_DATE_FORMAT).format(DISPLAY_DATE_FORMAT)
      )
    : undefined;

  return (
    <AntdCard title={texts.seasonTitle}>
      <CloseDayConfirmationModal
        isModalVisible={isConfirmationVisible}
        setIsModalVisible={setIsConfirmationVisible}
        confirmationFunction={confirmChange}
        text={confirmationText}
      />
      <Form form={form} layout="vertical" onFinish={onFinish}>
        <Form.Item
          name="range"
          label={texts.seasonRange}
          rules={[{ required: true, message: texts.requiredField }]}
        >
          <DatePicker.RangePicker format={DISPLAY_DATE_FORMAT} />
        </Form.Item>
        <Form.Item
          name="weekdays"
          label={texts.seasonWeekdays}
          extra={texts.seasonWeekdaysHint}
        >
          <Checkbox.Group options={weekdayOptions} />
        </Form.Item>
        <Form.Item
          name="isOpen"
          label={texts.seasonAction}
          rules={[{ required: true, message: texts.requiredField }]}
        >
          <Radio.Group>
            <Radio value>{texts.open}</Radio>
            <Radio value={false}>{texts.close}</Radio>
          </Radio.Group>
        </Form.Item>
        <Form.Item>
          <Button type="primary" htmlType="submit" text={texts.seasonButton} />
        </Form.Item>
      </Form>
    </AntdCard>
  );
};

export default SeasonForm;
